import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useB2C } from "@/context/B2CAuthContext";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { RejectReasonModal } from "@/components/Verification";
import { CheckCircle2, XCircle, ShieldCheck, PackageSearch } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { FindoSpinner } from "@/components/FindoSpinner";

export default function ValiderReclamation() {
  const { t } = useLang();
  const { claimId } = useParams();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const { user } = useB2C();
  const [claim, setClaim] = useState(null);
  const [state, setState] = useState("loading"); // loading | ready | notfound | done
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [rejectOpen, setRejectOpen] = useState(false);

  useEffect(() => {
    let alive = true;
    const load = token
      ? api(`/public/verification-claims/${claimId}?token=${encodeURIComponent(token)}`, { auth: false })
      : api(`/b2c/verification-claims/${claimId}`, { b2c: true });
    load.then((c) => { if (!alive) return; setClaim(c); setState(c.status === "pending" ? "ready" : "done"); if (c.status !== "pending") setResult(c.status); })
      .catch(() => { if (alive) setState("notfound"); });
    return () => { alive = false; };
  }, [claimId, token]);

  async function decide(decision, reason = "") {
    setBusy(true);
    try {
      const res = token
        ? await api(`/public/verification-claims/${claimId}/decide`, { method: "POST", auth: false, body: { token, decision, reason } })
        : await api(`/b2c/verification-claims/${claimId}/${decision === "approve" ? "approve" : "reject"}`, { method: "POST", b2c: true, body: { reason } });
      setResult(res.status || (decision === "approve" ? "approved" : "rejected"));
      setRejectOpen(false);
      setState("done");
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  }

  return (
    <div className="min-h-screen bg-findo-cream">
      <header className="border-b border-findo-navy/10 bg-white/80 backdrop-blur">
        <div className="max-w-2xl mx-auto px-5 py-4 flex items-center justify-between">
          <Link to="/"><Logo /></Link>
          {user && <Link to="/mon-compte" className="text-sm font-semibold text-findo-navy hover:text-findo-red">{t("hc.myAccount")}</Link>}
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-5 py-10">
        {state === "loading" && (
          <div className="grid place-items-center py-24 text-findo-navy/60" data-testid="valider-loading"><FindoSpinner size={28} className="mb-3" /> {t("hc.loading")}</div>
        )}

        {state === "notfound" && (
          <div className="bg-white rounded-2xl border border-border p-8 text-center card-shadow" data-testid="valider-notfound">
            <PackageSearch className="mx-auto text-findo-navy/40 mb-3" size={40} />
            <h1 className="font-display font-bold text-xl text-findo-navy mb-2">{t("hc.claimNotFound")}</h1>
            <p className="text-slate-500 text-sm">{t("hc.thisValidationLinkIsInvalid")}</p>
          </div>
        )}

        {state === "done" && (
          <div className="bg-white rounded-2xl border border-border p-8 text-center card-shadow" data-testid="valider-done">
            {result === "approved" ? <CheckCircle2 className="mx-auto text-emerald-500 mb-3" size={44} /> : <XCircle className="mx-auto text-red-500 mb-3" size={44} />}
            <h1 className="font-display font-bold text-xl text-findo-navy mb-2">
              {result === "approved" ? t("hc.claimApproved") : t("hc.claimRejected")}
            </h1>
            <p className="text-slate-500 text-sm">
              {result === "approved"
                ? t("hc.theClaimantHasBeenNotified")
                : t("hc.theClaimantHasBeenInformed")}
            </p>
          </div>
        )}

        {state === "ready" && claim && (
          <div data-testid="valider-card">
            <p className="text-sm font-semibold text-findo-red mb-1 flex items-center gap-1.5"><ShieldCheck size={16} /> {t("hc.identityVerification")}</p>
            <h1 className="font-display font-bold text-2xl sm:text-3xl text-findo-navy mb-2">{claim.flow === "lost" ? t("hc.theOwnerDescribesTheirItem") : t("hc.aClaimToValidate")}</h1>
            <p className="text-slate-500 mb-6 text-sm">
              {claim.claimant_name ? <b>{claim.claimant_name}</b> : t("hc.aUser")} {claim.flow === "lost" ? t("hc.recognizedThePhotoYouSent") : t("hc.claimsToBeTheOwner")}
            </p>

            <div className="bg-white rounded-2xl border border-border p-6 card-shadow space-y-4">
              {(claim.answers || []).map((a, i) => (
                <div key={i} data-testid={`valider-answer-${i}`}>
                  <p className="text-xs uppercase tracking-wide text-slate-400 font-semibold">{a.question || `${t("hc.characteristic")} ${i + 1}`}</p>
                  <p className="text-findo-navy font-medium">{a.answer}</p>
                </div>
              ))}
              {(!claim.answers || claim.answers.length === 0) && <p className="text-slate-400 text-sm">{t("hc.noAnswerProvided")}</p>}
            </div>

            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <Button onClick={() => decide("approve")} disabled={busy} className="rounded-full flex-1 h-12 text-base bg-emerald-600 hover:bg-emerald-700 gap-2" data-testid="valider-approve-btn">
                <CheckCircle2 size={18} /> {t("hc.approveTheClaim")}
              </Button>
              <Button onClick={() => setRejectOpen(true)} disabled={busy} variant="outline" className="rounded-full flex-1 h-12 text-base gap-2 border-red-300 text-red-600 hover:bg-red-50" data-testid="valider-reject-btn">
                <XCircle size={18} /> {t("hc.rejectTheClaim")}
              </Button>
            </div>
            <RejectReasonModal open={rejectOpen} onOpenChange={setRejectOpen} busy={busy} onConfirm={(reason) => decide("reject", reason)} />
          </div>
        )}
      </main>
    </div>
  );
}
